import { APIStatus } from "@/api/APIStatus.type";
import { updateUserEvolutionStates as updateUserEvolutionStatesApi } from "@/api/privateRequests/evolutions/setUserEvolutionState";
import { reportContractViolation } from "@/api/reportContractViolation";
import {
  PayloadAction,
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import {
  type EvolutionsOverviewSort,
  isExpiringSoon as isExpiringSoonAt,
} from "@repo/shared/evolutions";
import {
  type EvolutionsSliceState as SharedEvolutionsSliceState,
  evolutionsInitialState,
  setEvolutionsContractReporter,
  evolutionsReducer as sharedEvolutionsReducer,
} from "@repo/shared/redux";
import type { Cost, EvoPathId } from "@repo/shared/types";
import type {
  EvolutionState,
  Evolution as SharedEvolution,
} from "@repo/shared/types";

import { MetaRatingsPlayer } from "../players/playersSlice";
import { SquadBuilderPlayerType } from "../squadBuilder/squadBuilderUtils";
import { IRootState } from "../store";

/*
  The evolution catalogue types are defined once, in @repo/shared/types, so the
  web and mobile evolution features cannot drift apart. They are re-exportedps/web/src/redux/evolutions/evolutionsSlice.ts:8:1
  here because ~20 web modules already import them from this slice; point new
  code at @repo/shared/types directly.
*/
export type {
  EvolutionCategory,
  EvolutionSource,
  EvolutionState,
  GenericUpgrade,
  PlayStyleUpgrade,
  Requirement,
  RewardCategory,
  TopPlayer,
  Upgrade,
} from "@repo/shared/types";

export type Evolution = SharedEvolution;

export const isEvolutionCompleted = (evo: Evolution) =>
  evo.state === "completed";

// Lives here (not in a component file) so react-refresh can Fast Refresh the
// components that use it — mixed component/function exports invalidate HMR.
// The rule itself is shared; this only pins "now" to the call.
export const isExpiringSoon = (evo: Evolution) =>
  isExpiringSoonAt(evo, Date.now());

/*
  The catalogue itself — the fetch, the lists, and the picker's include /
  must-include selection — is shared with the app (DEV-3455). Web composes
  on top: the overview sort and the user-state update are web-only, and every
  other action is handed to the shared reducer.
*/
type EvolutionsSliceState = SharedEvolutionsSliceState & {
  updateEvolutionsStatus: APIStatus;
  overviewSort: EvolutionsOverviewSort;
};

const initialState: EvolutionsSliceState = {
  ...evolutionsInitialState,
  updateEvolutionsStatus: "idle",
  overviewSort: "new",
};

// Web sends the backend's half-filled `topPlayers` entries to Sentry.
setEvolutionsContractReporter(reportContractViolation);

export {
  applyEvolutionSelection,
  fetchEvolutions,
  invalidateEvolutions,
  setActivePickerSelection,
  setIncludedEvolutions,
  setIncludedEvolutionsByState,
  setMustIncludeEvolutions,
  setMustIncludeEvolutionsByState,
} from "@repo/shared/redux";

export type EvolutionStateItem = {
  id: number;
  categoryId: number;
  state: EvolutionState;
};

export const updateUserEvolutionStates = createAsyncThunk(
  "evolutions/setState",
  async ({ evolutions }: { evolutions: EvolutionStateItem[] }) => {
    await updateUserEvolutionStatesApi({
      evolutions,
    });
    return evolutions;
  },
);

const evolutionsSlice = createSlice({
  name: "evolutions",
  initialState,
  reducers: {
    resetUpdateEvolutionsStatus: (state) => {
      state.updateEvolutionsStatus = "idle";
    },
    setOverviewSort: (state, action: PayloadAction<EvolutionsOverviewSort>) => {
      state.overviewSort = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateUserEvolutionStates.pending, (state) => {
        state.updateEvolutionsStatus = "pending";
      })
      .addCase(updateUserEvolutionStates.rejected, (state) => {
        state.updateEvolutionsStatus = "rejected";
      })
      .addCase(updateUserEvolutionStates.fulfilled, (state, action) => {
        state.evolutions = [
          ...state.evolutions.map((evo) => {
            const updatedEvo = action.payload.find((e) => e.id === evo.id);
            return {
              ...evo,
              state: updatedEvo?.state ?? evo.state,
            };
          }),
        ];
        state.updateEvolutionsStatus = "fulfilled";
      })
      // The catalogue, the fetch and the picker selection: shared's business.
      // RTK reducers accept an Immer draft, so the web-only fields ride along.
      .addDefaultCase((state, action) => {
        sharedEvolutionsReducer(state, action);
      });
  },
});

export const getEvolutionByIdSelector = (id: number) => (state: IRootState) => {
  const { evolutions, expiredEvolutions } = state.evolutionsReducer;
  const evolution = evolutions.find((evo) => evo.id === id);
  if (evolution) {
    return evolution;
  }
  const expiredEvolution = expiredEvolutions.find((evo) => evo.id === id);
  if (expiredEvolution) {
    return { ...expiredEvolution, expired: true };
  }
};

export const evolutionsSelector = (state: IRootState) =>
  state.evolutionsReducer;

export const evolutionCategoriesByIdSelector = (state: IRootState) =>
  state.evolutionsReducer.categories;

export const evolutionCategoriesOrderedSelector = createSelector(
  evolutionCategoriesByIdSelector,
  (categoriesById) =>
    Object.values(categoriesById).sort((a, b) => b.priority - a.priority),
);

export const includesAtLeastOneExpiredEvolutionSelector =
  ({ ids }: { ids: number[] }) =>
  (state: IRootState) => {
    const { expiredEvolutions } = state.evolutionsReducer;
    return ids.some((id) => expiredEvolutions.some((evo) => evo.id === id));
  };

export const { resetUpdateEvolutionsStatus, setOverviewSort } =
  evolutionsSlice.actions;

export const overviewSortSelector = (state: IRootState) =>
  state.evolutionsReducer.overviewSort;

export const includedEvolutionsSelector = (state: IRootState) =>
  state.evolutionsReducer.includedEvolutions;

export const mustIncludeEvolutionsSelector = (state: IRootState) =>
  state.evolutionsReducer.mustIncludeEvolutions;

export default evolutionsSlice.reducer;
